import * as THREE from 'three';
import { Stats } from './Stats';
import { Inventory } from './Inventory';
import type { World } from '../world/World';
import type { InputManager } from '../input/InputManager';
import { itemDef } from '../data/items';
import { WATER_LEVEL } from '../world/WorldGen';

/**
 * Oyuncu kontrolcüsü.
 * Fizik analitik arazi yüksekliğine dayanır (mesh raycast yok) — mobilde çok daha ucuz.
 * Eğilme hem hitbox'ı hem kamera yüksekliğini değiştirir.
 */

export interface PlayerSave {
  x: number;
  y: number;
  z: number;
  yaw: number;
  pitch: number;
  kogox: number;
  stats: ReturnType<Stats['toSave']>;
  inventory: ReturnType<Inventory['toSave']>;
  playtime: number;
}

const STAND_HEIGHT = 1.78;
const CROUCH_HEIGHT = 1.15;
const EYE_RATIO = 0.92;
const RADIUS = 0.42;
const GRAVITY = -24;
const JUMP_SPEED = 8.2;
const WALK_SPEED = 4.4;
const SPRINT_MULT = 1.85;
const CROUCH_MULT = 0.45;
const SWIM_SPEED = 3.2;

export class Player {
  readonly position = new THREE.Vector3(0, 0, 0);
  readonly velocity = new THREE.Vector3();
  readonly stats = new Stats();
  readonly inventory = new Inventory();
  kogox = 0;
  playtime = 0;

  yaw = 0;
  pitch = 0;
  height = STAND_HEIGHT;
  crouching = false;
  grounded = false;
  swimming = false;
  /** Bu karede yatay hareket girdisi var mı (ayak sesi zamanlaması için). */
  moving = false;
  sprinting = false;
  /** Bu karede yere değdi mi (önceki karede havadaydı) — iniş sesi tetikler. */
  justLanded = false;
  /** Bu karede zıplama gerçekleşti mi — zıplama sesi tetikler. */
  justJumped = false;
  /** Bindiği canlı (fok gibi) varsa id'si. */
  mountId: string | null = null;

  private bobTime = 0;
  private wantJump = false;
  /** Saldırı/vuruş animasyonu ilerlemesi (0 = dinlenme, 1 = tam vuruş anı). */
  private swingAnim = 0;
  private swingSide = 1;
  /** Kamera altında görünen birinci şahıs el/alet modeli. */
  private readonly heldView = new THREE.Group();
  private heldItem = '';

  constructor(readonly camera: THREE.PerspectiveCamera) {
    this.heldView.name = 'held-item';
    this.heldView.renderOrder = 1000;
    this.camera.add(this.heldView);
    // Seçim/değişim simülasyon karesini beklemeden görünür modele yansısın.
    // Seçim olayı ayrıca dinlenir: bazı çağıranlar yalnız hotbar seçimini değiştirir.
    const refreshHeldView = () => {
      this.heldItem = '\u0000';
      this.syncHeldView(0, 0);
    };
    this.inventory.events.on('selection:changed', refreshHeldView);
    this.inventory.events.on('changed', refreshHeldView);
  }

  /** Dünya kapatılırken kamera altında kalan viewmodeli temizler. */
  dispose(): void {
    this.camera.remove(this.heldView);
    this.heldView.clear();
  }

  get eyeHeight(): number {
    return this.height * EYE_RATIO;
  }

  get eyePosition(): THREE.Vector3 {
    return new THREE.Vector3(this.position.x, this.position.y + this.eyeHeight, this.position.z);
  }

  /**
   * Bakış yönü (normalize).
   * three.js kamerası yerel -Z eksenine bakar; rotation.y = yaw uygulandığında
   * dünya yönü (-sin yaw, ., -cos yaw) olur. İşaretler bu yüzden negatiftir —
   * aksi halde nişan alınan yön kameranın tam tersi olur.
   */
  forward(out = new THREE.Vector3()): THREE.Vector3 {
    const cp = Math.cos(this.pitch);
    out.set(-Math.sin(this.yaw) * cp, Math.sin(this.pitch), -Math.cos(this.yaw) * cp);
    return out.normalize();
  }

  /** Sağ yönü (yalpalama için); kameranın yerel +X ekseninin dünya karşılığı. */
  right(out = new THREE.Vector3()): THREE.Vector3 {
    out.set(Math.cos(this.yaw), 0, -Math.sin(this.yaw));
    return out.normalize();
  }

  requestJump(): void {
    this.wantJump = true;
  }

  /**
   * Görünür bir kol/silah modeli olmadığı için saldırı/toplama "hissi" kamera
   * üzerinden verilir: kısa bir aşağı-öne kick animasyonu tetiklenir.
   * update() içinde her karede sönümlenerek kameraya uygulanır.
   */
  triggerSwing(): void {
    this.swingAnim = 1;
    this.swingSide *= -1; // ardışık vuruşlar aynı yöne değil, alternatif tarafa sallanır
  }

  /** Doğma / ışınlanma. */
  placeAt(world: World, x: number, z: number): void {
    const y = Math.max(WATER_LEVEL + 0.5, world.heightAt(x, z));
    this.position.set(x, y, z);
    this.velocity.set(0, 0, 0);
  }

  update(dt: number, world: World, input: InputManager): void {
    this.playtime += dt;
    this.justLanded = false;
    this.justJumped = false;
    const wasGrounded = this.grounded;

    // --- bakış ---
    const look = input.consumeLook();
    this.yaw += look.dx;
    this.pitch = clamp(this.pitch + look.dy, -1.45, 1.45);

    // --- eğilme ---
    const wantCrouch = input.isDown('crouch');
    if (wantCrouch !== this.crouching) {
      // Ayağa kalkarken üstte yer var mı diye bakılır (basit kontrol)
      this.crouching = wantCrouch;
    }
    const targetHeight = this.crouching ? CROUCH_HEIGHT : STAND_HEIGHT;
    this.height += (targetHeight - this.height) * Math.min(1, dt * 12);

    // --- hareket ---
    const mv = input.moveVector();
    const moving = Math.abs(mv.x) > 0.01 || Math.abs(mv.y) > 0.01;
    const waterHere = world.heightAt(this.position.x, this.position.z) < WATER_LEVEL - 0.3;
    this.swimming = waterHere && this.position.y < WATER_LEVEL + 0.2;

    const sprinting = input.isDown('sprint') && moving && !this.crouching && this.stats.canSprint();
    // Gerçek hareket denendikten sonra moving/sprinting atanır.
    this.moving = false;
    this.sprinting = false;
    let speed = WALK_SPEED * this.stats.modifier('speed');
    if (this.crouching) speed *= CROUCH_MULT;
    else if (sprinting) speed *= SPRINT_MULT;
    if (this.swimming) speed = SWIM_SPEED;
    // Bataklık/soğuk yavaşlatması
    if (this.stats.hasEffect('slow')) speed *= 1 - Math.min(0.6, this.stats.modifier('slow'));
    if (this.stats.hunger <= 0) speed *= 0.65;

    const sin = Math.sin(this.yaw);
    const cos = Math.cos(this.yaw);
    // İleri = kameranın baktığı yön (yatay): (-sin, -cos). Sağ = (cos, -sin).
    // Önceki sürümde işaretler tersti; W tuşu geriye yürütüyordu.
    const dirX = -mv.y * sin + mv.x * cos;
    const dirZ = -mv.y * cos - mv.x * sin;

    const stepX = dirX * speed * dt;
    const stepZ = dirZ * speed * dt;

    const beforeX = this.position.x;
    const beforeZ = this.position.z;
    // Eksen ayrı ayrı denenir: duvara sürtünürken tamamen kilitlenme olmaz
    this.tryMove(world, stepX, 0);
    this.tryMove(world, 0, stepZ);
    // Ses/bob yalnız gerçekten yer değiştiren oyuncuda çalışır; duvara W ile
    // dayanmak artık Windows'ta seri tuş sesi gibi footstep üretmez.
    const moved = Math.hypot(this.position.x - beforeX, this.position.z - beforeZ);
    this.moving = moved > 0.001 && !this.swimming;
    this.sprinting = sprinting && this.moving;

    // --- dikey ---
    const ground = world.supportHeightAt(this.position.x, this.position.z, this.position.y);
    if (this.swimming) {
      // Suda yüzme: yavaşça yüzeye çıkar, zıplama tuşu ile yükselir
      const target = WATER_LEVEL - 0.35;
      this.velocity.y += (target - this.position.y) * 4 * dt;
      if (this.wantJump) this.velocity.y = 2.6;
      this.velocity.y = clamp(this.velocity.y, -3, 3);
      this.position.y += this.velocity.y * dt;
      this.grounded = false;
    } else {
      if (this.wantJump && this.grounded && !this.crouching && this.stats.stamina > 4) {
        this.velocity.y = JUMP_SPEED;
        this.stats.stamina = Math.max(0, this.stats.stamina - 6);
        this.grounded = false;
        this.justJumped = true;
      }
      this.velocity.y += GRAVITY * dt;
      this.position.y += this.velocity.y * dt;
      if (this.position.y <= ground) {
        this.position.y = ground;
        this.velocity.y = 0;
        this.grounded = true;
        if (!wasGrounded) this.justLanded = true;
      } else {
        this.grounded = false;
      }
    }
    this.wantJump = false;

    // --- istatistikler ---
    const biome = world.biomeAt(this.position.x, this.position.z);
    this.stats.update(dt, {
      moving,
      sprinting,
      difficulty: world.params.difficulty,
      inHazard: biome.biome.hazard ?? null,
    });

    // --- kamera ---
    this.bobTime += dt * (sprinting ? 11 : 7) * (moving && this.grounded ? 1 : 0);
    const bob = moving && this.grounded ? Math.sin(this.bobTime) * (sprinting ? 0.055 : 0.03) : 0;

    // Saldırı/vuruş kick'i: hızla 1'e sıçrar, sonra sönümlenerek 0'a döner
    // (görünür kol/alet modeli yok; bu kamera hareketi vuruş anının "hissi"ni verir).
    this.swingAnim = Math.max(0, this.swingAnim - dt * 5);
    const swing = Math.sin(Math.min(1, this.swingAnim) * Math.PI) * this.swingSide;

    this.camera.position.set(this.position.x, this.position.y + this.eyeHeight + bob - Math.abs(swing) * 0.05, this.position.z);
    this.camera.rotation.order = 'YXZ';
    this.camera.rotation.y = this.yaw + swing * 0.05;
    this.camera.rotation.x = this.pitch - Math.abs(swing) * 0.045;
    this.camera.rotation.z = (moving && this.grounded ? Math.sin(this.bobTime * 0.5) * 0.006 : 0) + swing * 0.03;
    this.syncHeldView(swing, bob);
  }

  private syncHeldView(swing: number, bob: number): void {
    const held = this.inventory.held();
    const item = held?.item ?? '';
    // Kırılma durumu da anahtara girer: aynı eşya kırıldığında model rengi
    // yenilenmeli, yoksa sağlam görünümü ekranda kalır.
    const key = item ? `${item}${held?.broken ? '#broken' : ''}` : '';
    if (key !== this.heldItem) {
      this.heldItem = key;
      this.heldView.clear();
      if (item) {
        const tool = itemDef(item).tool;
        // Kırık alet elde görünür kalır (yok sanılmasın) ama koyu/kızıl tonla
        // hasarlı olduğu belli olur. Hasar gücü zaten yumruk seviyesindedir.
        const color = held?.broken
          ? 0x5a3a34
          : tool?.class === 'sword'
            ? 0xd9e4ec
            : tool?.class === 'bow'
              ? 0x8b5a2b
              : tool?.class === 'pickaxe'
                ? 0x9aa1a8
                : tool?.class === 'axe'
                  ? 0x9c6a3a
                  : 0xb77942;
        // Viewmodel ışık/zemin tarafından örtülmez; kamera katmanında her
        // zaman okunaklıdır. Sınıfa göre sap+başlık görünümü kullanılır.
        const mat = new THREE.MeshBasicMaterial({ color, depthTest: false, depthWrite: false });
        const hand = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.24, 0.18), new THREE.MeshBasicMaterial({ color: 0xffc6a6, depthTest: false, depthWrite: false }));
        hand.position.set(0.28, -0.28, -0.55);
        const shaft = new THREE.Mesh(new THREE.BoxGeometry(0.09, tool?.class === 'sword' ? 0.65 : 0.78, 0.09), mat);
        shaft.position.set(0.34, -0.08, -0.7);
        const head = new THREE.Mesh(new THREE.BoxGeometry(tool?.class === 'pickaxe' ? 0.52 : tool?.class === 'axe' ? 0.38 : tool?.class === 'sword' ? 0.13 : 0.24, 0.12, 0.14), mat);
        head.position.set(0.34, 0.27, -0.7);
        for (const part of [hand, shaft, head]) {
          part.renderOrder = 1000;
          this.heldView.add(part);
        }
      }
    }
    this.heldView.visible = !!item;
    this.heldView.position.set(0.05 * Math.sin(this.bobTime), bob * 0.45 - Math.abs(swing) * 0.12, 0);
    this.heldView.rotation.set(-0.18 - Math.abs(swing) * 0.85, -0.28 + swing * 0.65, -0.12 + swing * 0.32);
  }

  /** Yatay hareket denemesi: arazi eğimi ve yapı çarpışması kontrol edilir. */
  private tryMove(world: World, dx: number, dz: number): void {
    if (dx === 0 && dz === 0) return;
    const nx = this.position.x + dx;
    const nz = this.position.z + dz;
    if (world.blockedAt(nx, nz, this.position.y, RADIUS)) return;
    // Çok dik yamaç tırmanılamaz (yüzerken serbest)
    if (!this.swimming) {
      const h = world.supportHeightAt(nx, nz, this.position.y);
      const rise = h - this.position.y;
      if (rise > 0.9) return;
    }
    this.position.x = nx;
    this.position.z = nz;
  }

  /** Elde seçili, kırılmamış araç varsa hasarı; yumruk = 3. */
  attackDamage(): number {
    const held = this.inventory.held();
    if (held?.broken) return 3;
    const tool = held ? itemDef(held.item).tool : null;
    const base = tool?.damage ?? 3;
    return base * this.stats.modifier('strength');
  }

  toSave(): PlayerSave {
    return {
      x: this.position.x,
      y: this.position.y,
      z: this.position.z,
      yaw: this.yaw,
      pitch: this.pitch,
      kogox: Math.max(0, Math.round(this.kogox)),
      stats: this.stats.toSave(),
      inventory: this.inventory.toSave(),
      playtime: this.playtime,
    };
  }

  loadSave(data: Partial<PlayerSave> | undefined, world: World): void {
    const n = (v: unknown, def: number) => (Number.isFinite(v as number) ? (v as number) : def);
    this.position.set(n(data?.x, 0), n(data?.y, 0), n(data?.z, 0));
    // Konum bozuksa/haritanın dışındaysa güvenli yere al
    if (!Number.isFinite(this.position.x) || Math.abs(this.position.x) > 1e6) this.position.set(0, 0, 0);
    const ground = world.heightAt(this.position.x, this.position.z);
    if (this.position.y < ground - 2 || this.position.y > ground + 200) this.position.y = ground;
    this.yaw = n(data?.yaw, 0);
    this.pitch = clamp(n(data?.pitch, 0), -1.45, 1.45);
    this.kogox = Math.max(0, n(data?.kogox, 0));
    this.playtime = Math.max(0, n(data?.playtime, 0));
    this.stats.loadSave(data?.stats);
    this.inventory.loadSave(data?.inventory);
    this.velocity.set(0, 0, 0);
  }
}

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}
